import { tokens, TextInput } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { useCallback, useContext, useState } from 'react';
import type { KeyboardEventHandler, KeyboardEvent, FocusEvent } from 'react';
import { localeNumber } from '@hotjar/insights-common/lib/number';
import { useTranslation } from '@hotjar/translations';
import { SideBarContext } from './SideBar';
import { EntityActionsDropdown } from '../EntityActions/EntityActionsDropdown';

const Count = styled.span(
  ({ alwaysVisible }: { alwaysVisible?: boolean }) => `
  ${alwaysVisible === true ? 'visibility: visible !important;' : ''}
  color: inherit;
  flex-shrink: 0;
  min-width: 0;
`,
);

const Title = styled.span`
  color: inherit;
  flex-shrink: 1;
  min-width: 0;
  text-align: left;
  ${tokens.truncateText()};
  max-width: calc(100% - 32px);
`;

const Menu = styled.div`
  visibility: hidden;
  position: absolute;
  top: 0;
  right: 0;
  margin: -6px -${tokens.spacer.x0pt5};

  &:focus {
    visibility: visible;
  }
`;

const ItemLink = styled(Link)(
  ({ selected }: { selected: boolean }) => `
  color: ${tokens.color.text_default};

  cursor: pointer;

  min-height: ${tokens.spacer.x4};

  display: flex;
  flex-direction: row;
  align-self: stretch;
  align-items: center;
  justify-content: space-between;
  gap: ${tokens.spacer.x1};
  padding: 6px ${tokens.spacer.x1};

  border-radius: ${tokens.radius.m};
  border: none;
  background: none;

  &:focus {
    outline: none;
    ${tokens.focus()};
  }

  ${tokens.transition('background-color')};
  ${tokens.transition('box-shadow')};

  ${tokens.text('body2')};
  
  text-decoration: none;
  /* Menu should show if item is hovered, has keyboard focus, or if a child element is focused*/
  &:hover, &:focus:focus-visible, &:not(:focus):focus-within {
    text-decoration: none;

    & ${Menu} {
      visibility: visible;
    }
    & ${Count} {
      visibility: hidden;
    }
  }

  &:not(:focus-visible) {
    outline: none;
    box-shadow: none;
  }

  &:hover {
    background: ${tokens.color.neutral_100};
    color: inherit;
  }

  &:active {
      background: ${tokens.color.bg_active};
      color: inherit;
  }

  &:focus {
    color: inherit;
  }

  ${
    selected &&
    `
    background-color: ${tokens.BRAND_COLORS.cobalt_200};
    color: ${tokens.color.secondary_cta};

    & ${Title} {
      ${tokens.text('subtitle2')};
    }

    &:focus, &:hover {
        color: ${tokens.color.secondary_cta};
    }
  `
  }
`,
);

const CountAndActions = styled.div`
  flex-shrink: 0;
  position: relative;
  margin-left: ${tokens.spacer.x0pt5};
  text-align: right;
`;

export type SideBarItemActions = 'edit' | 'copy_link' | 'delete' | 'duplicate' | 'rename';

export interface SideBarItemProps {
  title: string;
  count: number;
  href: string;
  id: number;
  nameMaxLength: number;
  hideMenu?: boolean;
  onDelete?: () => void;
  onCopyLink?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onRename?: () => void;
}

const InlineTextEdit = ({
  originalText,
  nameMaxLength,
  onEdit,
  onCancel,
}: {
  originalText: string;
  nameMaxLength: number;
  onEdit: (newText: string) => void;
  onCancel: () => void;
}): JSX.Element => {
  const [text, setText] = useState<string>(originalText);

  const onComplete = useCallback(
    (newValue: string) => {
      if (!newValue || newValue === originalText) {
        onCancel();
        return;
      }
      onEdit(newValue);
    },
    [onEdit, onCancel, originalText],
  );

  const onBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      onComplete(e.currentTarget.value);
    },
    [onComplete],
  );

  const onKeyDown: KeyboardEventHandler = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onComplete(e.currentTarget.value);
        return;
      }
      if (e.key === 'Escape') {
        onCancel();
      }
    },
    [onComplete, onCancel],
  );

  return (
    // I think auto-focus is reasonable here as it would be otherwise difficult for a screenreader user to navigate to the textbox
    // and the focus switch is in response to an explicit user action.
    // The textbox is also aria-labelled to indicate it's purpose so I think this is a reasonable use-case.
    <TextInput
      aria-label="Rename widget"
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      size="s"
      value={text}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onChange={(e) => setText(e.target.value)}
      maxLength={nameMaxLength}
    />
  );
};

export const SideBarItem = ({
  title,
  count,
  href,
  id,
  nameMaxLength,
  hideMenu,
  onDelete,
  onCopyLink,
  onDuplicate,
  onEdit,
  onRename,
}: SideBarItemProps): JSX.Element => {
  /* eslint-disable react/destructuring-assignment */
  const context = useContext(SideBarContext);
  const isSelected = context.selectedWidgetId === id;
  const isRenaming = context.renamingWidgetId === id;
  const { t } = useTranslation('feedback', { keyPrefix: 'sidebar.widgets' });

  return (
    <ItemLink
      title={title}
      aria-label={title}
      aria-selected={isSelected}
      selected={isSelected}
      to={href}
    >
      {isRenaming ? (
        <InlineTextEdit
          nameMaxLength={nameMaxLength}
          originalText={title}
          onEdit={(newText) => context.rename?.(id, newText)}
          onCancel={() => context.cancelRename?.()}
        />
      ) : (
        <Title>{title}</Title>
      )}

      <CountAndActions>
        {hideMenu === true ? null : (
          <Menu onClick={(e) => e.preventDefault()}>
            <EntityActionsDropdown
              placement="bottom-end"
              onEdit={onEdit}
              onCopyLink={onCopyLink}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onRename={onRename}
            />
          </Menu>
        )}
        <Count aria-label={t('count-label')} alwaysVisible={hideMenu}>
          {localeNumber(count)}
        </Count>
      </CountAndActions>
    </ItemLink>
  );
  /* eslint-enable react/destructuring-assignment */
};
